// LA PÁGINA QUE ABRE EL CLIENTE PARA FIRMAR — las piezas que se ven.
//
// Viven aquí, y no dentro de SignaturePortalPage.jsx, porque las usan DOS
// sitios: la página pública del cliente y la vista previa a pantalla completa
// del módulo de Firmas del CRM (Lissette 2026-09-23: *"queremos ver el previo
// de cómo le llegará al cliente para poder editarlo; esa vista previa es muy
// pequeña y no deja ver los errores y forma del párrafo"*).
//
// Si el CRM copiara estas piezas, la primera vez que alguien retocara una la
// vista previa volvería a enseñar algo distinto de lo que recibe el cliente —
// que es exactamente lo que se arregló en E-317 y la regla 208. Se comparte
// el componente, no los estilos.
import { Shield, FileText } from 'lucide-react';
import { partirPorPreguntas, equilibrarTrozosHtml } from '../marcadorDePregunta.js';
import { partirPorCamposDeFirma } from '../camposDeFirma.js';
import { esDocumentoConFormato } from '../documentoConFormato.js';
import HojaDelDocumento from './HojaDelDocumento.jsx';
import { safeHtml } from '../sanitizeHtml.js';

/** El fondo y el ancho de la página del cliente. El ancho decide dónde se
 *  parte cada línea: una vista previa más estrecha enseña otros párrafos. */
export const FONDO_DE_LA_PAGINA = 'linear-gradient(180deg,#0a1628 0%,#0d1e36 100%)';
export const ANCHO_DE_LA_PAGINA = 920;

export function fmtDate(d) {
  if (!d) return '';
  // Always en-US (mm/dd/yyyy convention). Date-only strings are pinned to
  // LOCAL midnight so the displayed day matches the recipient's wall clock;
  // full ISO timestamps parse as-is.
  try {
    const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('en-US');
  } catch { return ''; }
}

/** La cabecera: logo, agencia, saludo, nombre del documento y caducidad. */
export function CabeceraDeLaFirma({ data }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
      {data.agencyLogoUrl && <img src={data.agencyLogoUrl} alt={data.agencyName || ''} style={{ maxHeight: 64, maxWidth: 240, objectFit: 'contain', display: 'block', margin: '0 auto .85rem', background: '#fff', borderRadius: 10, padding: '8px 12px' }} />}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '.4rem 1rem', borderRadius: 999, background: 'rgba(0,212,255,.1)', color: '#00d4ff', fontSize: 12, fontWeight: 700, letterSpacing: '.5px' }}>
        <Shield size={14} /> {data.agencyName || 'SIGNATURE REQUEST'}
      </div>
      <h1 style={{ fontSize: '1.6rem', margin: '.8rem 0 .3rem' }}>Hi {data.clientName},</h1>
      <p style={{ color: '#7aa3c0', margin: 0 }}>Please review the document below and sign at the bottom.</p>
      <p style={{ color: '#e6f0fa', fontWeight: 700, fontSize: '1rem', margin: '4px 0 0' }}>{data.documentName}</p>
      {data.expiresAt && (
        <p style={{ color: '#45688a', fontSize: 12, marginTop: 6 }}>
          Link valid until {fmtDate(data.expiresAt)}
        </p>
      )}
    </div>
  );
}

// Render a Deskte template letter as a formal-looking document. The client
// sees it on a white "page" with serif body text — visually the same letter
// the agent sees in the CRM.
export function LetterPreview({ body, title, logoUrl, formatting, questions, responses, missing, onAnswer, campos }) {
  // El formato guardado de la plantilla (fuente / tamaño / alineacion) lo
  // aplica la HOJA, que es la misma que enseña la vista previa del CRM.
  const conFormato = esDocumentoConFormato(body);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={16} style={{ color: '#00d4ff' }} />
          <strong style={{ fontSize: 13, color: '#7aa3c0', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            Document
          </strong>
        </div>
      </div>
      {/* LA HOJA es el componente compartido: la MISMA que enseñan las dos
          vistas previas del CRM. Vivia aqui dentro, y por eso "asi se vera al
          enviarlo" no era verdad en ninguna de las dos (Lissette 2026-09-22).
          Ver crm-app/components/HojaDelDocumento.jsx. */}
      <HojaDelDocumento body={body} title={title} logoUrl={logoUrl} formatting={formatting}>
        {/* LAS PREGUNTAS CLAVADAS SALEN AQUI, EN SU SITIO DENTRO DEL TEXTO
            (Lissette 2026-08-28: "quiero colocar eso donde yo quiera"). El
            cuerpo se parte por sus marcadores y entre trozo y trozo se pinta el
            control. Un documento sin marcadores devuelve UN solo trozo de
            texto, asi que se pinta exactamente igual que antes. */}
        {/* Un documento CON FORMATO se pinta como HTML; uno de texto plano,
            con `pre-wrap`, que es lo que ha hecho siempre. La decision vive en
            `documentoConFormato.js` y la comparte con el servidor, que dibuja el
            PDF: si cada uno decidiera, el cliente veria una cosa y firmaria
            otra. El HTML pasa por `safeHtml` — el documento lo escribe el
            agente, pero esto es una pagina publica y ahi no entra nada sin
            limpiar. */}
        {(() => {
            // Y la firma, las iniciales y el nombre donde los puso la agente
            // (`{{f:...}}`, Lissette 2026-10-23): mismos trozos, otro tipo.
            const partes = partirPorCamposDeFirma(partirPorPreguntas(body, (questions || []).map((q) => q.id)));
            // Con formato, cada trozo se cierra y se reabre para que su HTML sea
            // valido por si solo: sin eso, el navegador cierra las etiquetas a su
            // manera y el documento se descoloca justo donde va la pregunta.
            return conFormato ? equilibrarTrozosHtml(partes) : partes;
          })().map((trozo, i) => (
            trozo.tipo === 'texto'
              ? (conFormato
                ? <span key={i} dangerouslySetInnerHTML={safeHtml(trozo.valor)} />
                : <span key={i}>{trozo.valor}</span>)
              : trozo.tipo === 'campo'
              ? <CampoDeFirmaEnLinea key={i} campo={trozo.campo} valores={campos} />
              : <PreguntaEnLinea
                  key={i}
                  q={(questions || []).find((q) => q.id === trozo.id)}
                  value={responses ? responses[trozo.id] : undefined}
                  onChange={(v) => onAnswer && onAnswer(trozo.id, v)}
                  missing={(missing || []).includes(trozo.id)}
                />
          ))}
      </HojaDelDocumento>
    </div>
  );
}

// LA FIRMA, LAS INICIALES O EL NOMBRE, EN SU SITIO DENTRO DEL DOCUMENTO
// (Lissette 2026-10-23). En el portal enseña lo que el cliente va escribiendo
// abajo (su nombre, sus iniciales); la firma se ve como el hueco donde irá,
// porque se dibuja una vez abajo y en el PDF sale en cada sitio. En las vistas
// previas del CRM, sin `valores`, sale el hueco con su rótulo.
function CampoDeFirmaEnLinea({ campo, valores }) {
  const v = valores || {};
  const hueco = {
    display: 'inline-flex', alignItems: 'flex-end', justifyContent: 'center', verticalAlign: 'bottom',
    minWidth: campo === 'firma' ? 150 : campo === 'iniciales' ? 56 : 160, minHeight: campo === 'firma' ? 34 : 22,
    margin: '0 .2rem', padding: '0 .3rem', borderBottom: '1px solid #1a1a1a',
    background: 'rgba(0,212,255,.08)', borderRadius: '4px 4px 0 0',
  };
  const rotulo = { fontSize: '.7em', color: '#5b6b7c', fontStyle: 'italic', fontFamily: 'Arial, sans-serif' };
  if (campo === 'nombre') {
    return <span style={hueco}>{v.nombre ? <span style={{ fontWeight: 600 }}>{v.nombre}</span> : <span style={rotulo}>Nombre y apellido / Full name</span>}</span>;
  }
  if (campo === 'iniciales') {
    return <span style={hueco}>{v.iniciales ? <span style={{ fontFamily: v.fuente || 'cursive', fontSize: '1.3em', lineHeight: 1 }}>{v.iniciales}</span> : <span style={rotulo}>Iniciales / Initials</span>}</span>;
  }
  return <span style={hueco}><span style={rotulo}>✍️ Firma / Signature</span></span>;
}

// LA MISMA PREGUNTA, PERO DENTRO DEL DOCUMENTO.
//
// Va sobre el papel blanco, no sobre el fondo oscuro del portal, asi que sus
// colores son los del documento — un control con los colores del portal ahi
// dentro se leeria como algo pegado encima, no como parte de la hoja.
//
// Y va EN LINEA (`inline-flex`): eso es lo que permite que quede a la derecha
// de la frase, como en un cuestionario de papel, en vez de partir el parrafo en
// dos. Si la agente lo pone en su propio renglon, queda en su propio renglon:
// la posicion la decide ella al insertarlo, no este componente.
//
// Una pregunta sin enunciado sale desnuda, que es justo el caso de
// *"sin necesidad de colocar pregunta"*: el enunciado ya esta escrito en el
// documento, unas palabras antes.
function PreguntaEnLinea({ q, value, onChange, missing }) {
  if (!q) return null;
  const borde = missing ? '#d92d5a' : '#c2ccd8';
  const chip = (activo) => ({
    padding: '.12rem .5rem', borderRadius: 6, cursor: 'pointer',
    border: `1px solid ${activo ? '#0f9d6f' : borde}`,
    background: activo ? 'rgba(15,157,111,.14)' : '#fff',
    color: activo ? '#0f9d6f' : '#5b6b7c', fontWeight: 700,
    fontSize: '.85em', fontFamily: 'inherit', lineHeight: 1.4,
  });
  const caja = {
    padding: '.1rem .4rem', borderRadius: 6, border: `1px solid ${borde}`,
    background: '#fff', color: '#1a1a1a', fontSize: '.9em', fontFamily: 'inherit',
    outline: 'none', minWidth: 90,
  };
  return (
    <span id={`q-${q.id}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, verticalAlign: 'middle', margin: '0 .15rem' }}>
      {q.label ? <span style={{ fontWeight: 600 }}>{q.label}{q.required && <span style={{ color: '#d92d5a' }}> *</span>}</span> : null}

      {q.type === 'yesno' && [['yes', 'Sí'], ['no', 'No']].map(([v, etiqueta]) => (
        <button key={v} type="button" onClick={() => onChange(v)} style={chip(value === v)}>{etiqueta}</button>
      ))}

      {q.type === 'checkbox' && (
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} style={{ cursor: 'pointer' }} />
      )}

      {q.type === 'radio' && (q.options || []).map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)} style={chip(value === o)}>{o}</button>
      ))}

      {q.type === 'select' && (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} style={caja}>
          <option value="">—</option>
          {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      )}

      {q.type !== 'yesno' && q.type !== 'checkbox' && q.type !== 'radio' && q.type !== 'select' && (
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} style={caja} />
      )}

      {!q.label && q.required && <span style={{ color: '#d92d5a' }}>*</span>}
    </span>
  );
}
